import { useState, useEffect } from 'react';
import AppButton from '../AppButton/AppButton';
import toast from 'react-hot-toast';

function DateSearch({ onSearch, onClear }) {
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [dateError, setDateError] = useState('');

    // Validate date range (max 3 months)
    const validateDateRange = (start, end) => {
        if (!start || !end) return false;

        const startDateObj = new Date(start);
        const endDateObj = new Date(end);

        // Check if end date is before start date
        if (endDateObj < startDateObj) {
            setDateError('End date must be after start date');
            return false;
        }

        // Calculate difference in months
        const monthsDiff = (endDateObj.getFullYear() - startDateObj.getFullYear()) * 12
                          + (endDateObj.getMonth() - startDateObj.getMonth());

        // Check if difference exceeds 3 months
        if (monthsDiff > 3) {
            setDateError('Date range cannot exceed 3 months');
            return false;
        }

        // Additional check for days to handle edge cases
        const daysDiff = Math.floor((endDateObj - startDateObj) / (1000 * 60 * 60 * 24));
        if (daysDiff > 90) {
            setDateError('Date range cannot exceed 3 months (90 days)');
            return false;
        }

        setDateError('');
        return true;
    };

    // Clear date error when dates change
    useEffect(() => {
        if (startDate && endDate) {
            validateDateRange(startDate, endDate);
        } else {
            setDateError('');
        }
    }, [startDate, endDate]);

    const handleSearch = () => {
        if (!startDate || !endDate) {
            toast.error('Please select both start and end dates');
            return;
        }

        if (!validateDateRange(startDate, endDate)) {
            toast.error(dateError);
            return;
        }

        if (onSearch) {
            onSearch(startDate, endDate);
        }
    };

    const handleClear = () => {
        setStartDate('');
        setEndDate('');
        setDateError('');
        if (onClear) {
            onClear();
        }
    };

    // Get today's date in YYYY-MM-DD format for max attribute
    const today = new Date().toISOString().split('T')[0];

    return (
        <div className="w-full">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                {/* Start Date */}
                <div className="flex flex-col gap-1.5">
                    <label className="text-xs sm:text-sm font-medium text-gray-700">From Date</label>
                    <input
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        max={today}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    />
                </div>

                {/* End Date */}
                <div className="flex flex-col gap-1.5">
                    <label className="text-xs sm:text-sm font-medium text-gray-700">To Date</label>
                    <input
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        max={today}
                        min={startDate}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    />
                </div>
            </div>

            {/* Show date range error */}
            {dateError && (
                <div className="mt-2 text-xs text-red-600 font-medium bg-red-50 px-3 py-2 rounded-lg border border-red-200">
                    {dateError}
                </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mt-3 sm:mt-4">
                <AppButton
                    buttontext="Search"
                    onClick={handleSearch}
                    disabled={!startDate || !endDate || !!dateError}
                    className="w-full sm:w-auto"
                />

                {(startDate || endDate) && (
                    <button
                        onClick={handleClear}
                        className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-600 hover:text-red-600 hover:bg-red-50 border border-gray-300 rounded-lg hover:border-red-300 transition-all duration-200 active:scale-95"
                        title="Clear dates"
                    >
                        Clear
                    </button>
                )}
            </div>
        </div>
    );
}

export default DateSearch;
